import React, { useContext, useMemo } from "react";
import { Box, Card, Chip, Divider, Stack, Typography } from "@mui/material";
import {
  CustomDetailsList,
  DebouncedSearch,
  Loader,
  LoadingButton,
  ServerPaginatedTable,
  useDropdownApis,
} from "../../../shared";
import { lookupColumnType } from "../hooks/useManageSegmentation";
import { CriteriaChips } from "./CriteriaChips";
import { AppContext } from "../../../store";
import { Link } from "react-router-dom";

export const SegmentBasicDetails = ({
  state,
  onPreviewGotoPage,
  onPreviewSetPageSize,
  onPreviewSearch,
  onExport,
  onRerun,
}) => {
  const { appState } = useContext(AppContext);

  const segment = state?.data;
  const operators = state?.operators ?? {};

  // Enrich API rules with `type` for CriteriaChips
  const enrichedRules = useMemo(() => {
    const rules = segment?.rules ?? [];
    const criterias = state?.criterias ?? {};
    return rules.map((rule) => ({
      ...rule,
      type: rule.type ?? lookupColumnType(criterias, rule.table, rule.column),
    }));
  }, [segment?.rules, state?.criterias]);

  const customerColumns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => {
          const fullName = [row.original.first_name, row.original.last_name]
            .filter(Boolean)
            .join(" ");

          return (
            <Link
              to={`/customers/details/${row.original.id}`}
              style={{
                color: "inherit",
                textDecoration: "underline",
                "&:hover": { color: "#0000EE" },
              }}
            >
              {fullName || "-"}
            </Link>
          );
        },
        disableSortBy: true,
      },
      {
        Header: "Email ID",
        accessor: "email",
        Cell: ({ row }) => row.original.email || "-",
        disableSortBy: true,
      },
      {
        Header: "Phone Number",
        accessor: "phone",
        Cell: ({ row }) => row.original.phone || "-",
        disableSortBy: true,
      },
    ],
    [],
  );

  const hasStores = enrichedRules.some((r) => r.column === "store_id");

  const { storeList } = useDropdownApis({ isStoreList: hasStores });

  const dropdownOptions = {
    payment_type: {
      options: appState?.globalData?.payment_types ?? [],
      valueKey: "value",
    },
    payment_status: {
      options: appState?.globalData?.order_statues ?? [],
      valueKey: "label",
    },
    favourite_store_id: {
      options: (storeList ?? []).map((s) => ({ value: s.id, label: s.name })),
      valueKey: "value",
    },
    store_id: {
      options: (storeList ?? []).map((s) => ({ value: s.id, label: s.name })),
      valueKey: "value",
    },
    product_type: {
      options: appState?.globalData?.product?.type ?? [],
      valueKey: "value",
    },
  };

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <>
      <CustomDetailsList
        title="Details"
        columns={[
          { title: "Segment Name", accessor: (data) => data.name || "-" },

          {
            title: "Description",
            accessor: (data) => data.description || "-",
          },
          {
            title: "Created By",
            accessor: (data) =>
              data.created_by?.name
                ? data.created_by.name
                : data.is_system
                  ? "System"
                  : "-",
          },
        ]}
        data={segment}
      />

      {/* Criteria + Customers card */}
      <Card
        sx={{
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Criteria chips section */}
        {enrichedRules.length > 0 && (
          <Stack
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: 1,
                borderColor: "divider",
                height: "65px",
              }}
            >
              <Typography variant="body2" fontWeight={500} p="16px">
                Criteria
              </Typography>
            </Box>
            <Stack
              sx={{
                p: "16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Chip
                size="small"
                label={
                  segment?.logical_operator === "AND"
                    ? "MATCH ALL"
                    : "MATCH ANY"
                }
              />
              <CriteriaChips
                rules={enrichedRules}
                operators={operators}
                allCriteria={state.criterias || {}}
                dropdownOptions={dropdownOptions}
              />
            </Stack>
          </Stack>
        )}

        <ServerPaginatedTable
          tableTitle={`Customers ${segment?.result_count ? `(${segment.result_count})` : ""}`}
          columns={customerColumns}
          data={state.previewCustomers}
          isLoading={state.previewLoading}
          pagination={state.previewPagination}
          sortByGlobal={[]}
          gotoPage={onPreviewGotoPage}
          setPageSize={onPreviewSetPageSize}
          defaultSearch={false}
          searchBar={false}
          manualSortBy={false}
          headerRightContent={
            <Stack direction="row" gap="12px" alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Last Rerun: {segment?.last_updated_at}
              </Typography>
              <LoadingButton
                loading={state.isRerunning}
                onClick={onRerun}
                size="small"
                startIcon={
                  <img
                    src="/icons/ic_rerun.svg"
                    alt="rerun"
                    width={24}
                    style={{ opacity: state.isRerunning ? 0.2 : 1 }}
                  />
                }
              >
                Rerun
              </LoadingButton>
              <Divider orientation="vertical" flexItem />
              <LoadingButton
                variant="text"
                size="small"
                loading={state.isExporting}
                onClick={onExport}
                startIcon={
                  <img
                    src="/icons/ic_export.svg"
                    alt="export"
                    width={18}
                    style={{
                      opacity:
                        state.previewCustomers?.length === 0 ||
                        state.isExporting
                          ? 0.2
                          : 1,
                    }}
                  />
                }
                disabled={state.previewCustomers.length === 0}
              >
                Export
              </LoadingButton>
            </Stack>
          }
          filterSection={
            <DebouncedSearch
              value={state.searchText}
              onChange={onPreviewSearch}
            />
          }
        />
      </Card>
    </>
  );
};
